'use client';

import { useEffect, useState } from 'react';
import { countryFromBrowserTimeZone } from '@/lib/country-from-timezone';
import { normalizeCountry } from '@/lib/regional/providers-config';

// The visitor's country, from the same /api/geo IP lookup the checkout uses,
// so a country sale (Diwali in India, Hanukkah in Israel) shows to exactly the
// visitors who will get it at checkout. One lookup per browser session: the
// answer is kept in sessionStorage and shared by every component that asks.
//
// Country level only, from the IP: never GPS, never a permission prompt.

const STORAGE_KEY = 'gnc_visitor_country';
const CODE_STORAGE_KEY = 'gnc_visitor_country_code';
let pending: Promise<{ name: string | null; code: string | null }> | null = null;

function readStored(key: string): string | null | undefined {
    try {
        const raw = window.sessionStorage.getItem(key);
        if (raw === null) return undefined;
        return raw || null;
    } catch {
        return undefined;
    }
}

function fetchGeo(): Promise<{ name: string | null; code: string | null }> {
    if (!pending) {
        pending = fetch('/api/geo')
            .then(res => (res.ok ? res.json() : null))
            .then((data: { country_name?: string | null; country_code?: string | null } | null) => {
                const name = typeof data?.country_name === 'string' && data.country_name ? data.country_name : null;
                const code = typeof data?.country_code === 'string' && /^[A-Za-z]{2}$/.test(data.country_code) ? data.country_code.toUpperCase() : null;
                // A failed lookup is not remembered, so the next page can try again.
                try {
                    if (name) window.sessionStorage.setItem(STORAGE_KEY, name);
                    if (code) window.sessionStorage.setItem(CODE_STORAGE_KEY, code);
                } catch { /* storage blocked */ }
                return { name, code };
            })
            .catch(() => ({ name: null, code: null }))
            .finally(() => { pending = null; });
    }
    return pending;
}

/**
 * The visitor's country name (ipapi spelling, e.g. "South Korea"), or null
 * while unknown. Only looks anything up when `enabled`, so pages with no
 * country sale running cost no lookup at all.
 */
export function useVisitorCountry(enabled: boolean): string | null {
    const [country, setCountry] = useState<string | null>(null);

    useEffect(() => {
        if (!enabled) return;
        const stored = readStored(STORAGE_KEY);
        if (stored !== undefined) {
            setCountry(stored);
            return;
        }
        let cancelled = false;
        fetchGeo().then(value => { if (!cancelled) setCountry(value.name); });
        return () => { cancelled = true; };
    }, [enabled]);

    return enabled ? country : null;
}

/**
 * getUserCountry(): the visitor's ISO country code for UI personalisation.
 * IP lookup first (shared, cached), then the device timezone, then null.
 * Presentation only - never a security decision.
 */
export async function getUserCountry(): Promise<string | null> {
    if (typeof window === 'undefined') return null;
    const storedCode = readStored(CODE_STORAGE_KEY);
    if (storedCode) return storedCode;
    const storedName = readStored(STORAGE_KEY);
    const fromStoredName = normalizeCountry(storedName);
    if (fromStoredName) return fromStoredName;
    const { code, name } = await fetchGeo();
    return code || normalizeCountry(name) || normalizeCountry(countryFromBrowserTimeZone());
}

/**
 * ISO country code plus whether the lookup has finished. `ready` with a null
 * country means "could not tell" (the global default applies); not ready
 * means "still asking", so callers can avoid flashing the wrong options.
 */
export function useUserCountryState(enabled = true): { country: string | null; ready: boolean } {
    const [state, setState] = useState<{ country: string | null; ready: boolean }>({ country: null, ready: false });

    useEffect(() => {
        if (!enabled) return;
        let cancelled = false;
        getUserCountry()
            .catch(() => null)
            .then(value => { if (!cancelled) setState({ country: value, ready: true }); });
        return () => { cancelled = true; };
    }, [enabled]);

    return enabled ? state : { country: null, ready: false };
}

/** ISO country code, or null while unknown / when it cannot be determined. */
export function useUserCountry(enabled = true): string | null {
    return useUserCountryState(enabled).country;
}
